import React, { useContext, useEffect, useState } from "react";
import UserContext from "../components/UserContext";
import Home_1 from "./home/home_1";
import { Provider } from "react-redux";
import store from "../store";
import { positions, transitions, Provider as AlertProvider } from "react-alert";
import AlertTemplate from "react-alert-template-basic";
import GoogleAnalytics from "../components/googleAnalaytics";
import { useRouter } from "next/router";
import { isCorporateLoggedIn, isCandidateLoggedIn } from "../utils/authTokens";

const options = {
  timeout: 5000,
  position: positions.BOTTOM_CENTER,
  transition: transitions.SCALE,
};

export default function Home() {
  const [isClient, setIsClient] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const router = useRouter();
  const { scrollRef } = useContext(UserContext);

  // Use the effect to only run client-side code
  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsClient(true); // This ensures the code runs only on the client-side
    }
  }, []);

  // Check if user is authenticated and redirect to appropriate dashboard
  useEffect(() => {
    if (!isClient || authChecked) return;
    
    setAuthChecked(true);
    
    // Check for corporate token first, then candidate token
    // This allows both to be logged in simultaneously
    const corporateLoggedIn = isCorporateLoggedIn();
    const candidateLoggedIn = isCandidateLoggedIn();
    
    if (corporateLoggedIn) {
      // Corporate user is logged in - redirect to corporate dashboard
      setIsRedirecting(true);
      router.replace("/corporate/dashboard");
      return;
    } else if (candidateLoggedIn) {
      // Candidate user is logged in - redirect to candidate portal
      setIsRedirecting(true);
      router.replace("/candidate/dashboard");
      return;
    }
  }, [isClient, authChecked, router]);

  // Scroll position management
  useEffect(() => {
    if (isClient && scrollRef?.current) {
      window.scrollTo(0, scrollRef.current.scrollPos);
      const handleScrollPos = () => {
        scrollRef.current.scrollPos = window.scrollY;
      };
      window.addEventListener("scroll", handleScrollPos);
      return () => {
        window.removeEventListener("scroll", handleScrollPos);
      };
    }
  }, [isClient, scrollRef]);

  // Check hostname before rendering (only run this on the client-side)
  if (isClient && !(
    window.location.hostname.includes("localhost") ||
    window.location.hostname.includes("credohire-repo-managed-staffing.vercel.app") ||
    window.location.hostname.includes("run.app") ||
    window.location.hostname.includes("127.0.0.1") ||
    window.location.hostname.includes("synthiahire.ai")
  )) {
    window.location.href = `${window.location.origin}/corporate/login`;
    return null; // Prevents rendering of Home_1
  }

  // Don't render homepage if we're redirecting to a dashboard
  if (isRedirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {isClient && (
        <>
          <GoogleAnalytics />
          <Provider store={store}>
            <AlertProvider template={AlertTemplate} {...options}>
              <Home_1 />
            </AlertProvider>
          </Provider>
        </>
      )}
    </>
  );
}
